import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Sparkle, PercentIcon, CrownSimple, ChatCircleDots, ArrowUpRight, CalendarBlank } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import PlansList from "@/components/app/PlansList";
import AutoPayCard from "@/components/app/AutoPayCard";
import { NotificationPrompt } from "@/components/app/NotificationPrompt";
import PushDiagnostic from "@/components/app/PushDiagnostic";

const ClientHome = () => {
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/client/me").then(({ data }) => setMe(data)).catch(() => {});
  }, []);

  if (!me) return <div className="text-zinc-500">Carregando...</div>;

  const due = me.current_due_date;
  const today = new Date();
  const dueDate = due ? new Date(due) : null;
  const daysToDue = dueDate ? Math.ceil((dueDate - today) / 86400000) : null;
  const settings = me.settings || {};

  return (
    <div className="space-y-5">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Olá,</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{me.name}</h1>
      </motion.div>

      {/* Plans (main + subaccounts) with batch selection */}
      <PlansList />

      {/* Highlight banner for the main plan due date */}
      {daysToDue !== null && me.status === "ativo" && (daysToDue < 0 || daysToDue <= 7) && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className={`border rounded-2xl p-5 card-soft flex items-center gap-4 ${
            daysToDue < 0 ? "bg-rose-50 border-rose-200" :
            daysToDue <= 3 ? "bg-amber-50 border-amber-200" :
            "bg-emerald-50 border-emerald-200"
          }`}>
            <div className={`w-12 h-12 rounded-xl grid place-items-center ${
              daysToDue < 0 ? "bg-rose-100 text-rose-700" :
              daysToDue <= 3 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
            }`}>
              <CalendarBlank size={22} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="font-medium">
                {daysToDue < 0
                  ? `Plano principal vencido há ${Math.abs(daysToDue)} dia(s)`
                  : daysToDue === 0
                  ? "Seu plano principal vence hoje"
                  : `Faltam ${daysToDue} dia(s) para o vencimento do plano principal`}
              </div>
              <div className="text-sm text-zinc-600 mt-0.5">
                Mantenha em dia para preservar seu nível {me.loyalty_tier.toUpperCase()} e descontos.
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatTile testid="stat-months" icon={Sparkle} label="Tempo como cliente" value={`${me.months_active} ${me.months_active === 1 ? "mês" : "meses"}`} />
        <StatTile testid="stat-tier" icon={CrownSimple} label="Nível" value={me.loyalty_tier.toUpperCase()} />
        <StatTile testid="stat-discount" icon={PercentIcon} label="Desconto" value={`${me.effective_discount_pct}%`} />
      </div>

      {/* AutoPay - only renders when MP is enabled */}
      <AutoPayCard planValue={me.plan_value} dueDate={me.current_due_date} />

      {/* Push diagnostic — shows when notifications fail to register */}
      <PushDiagnostic />

      {/* First-time notification permission prompt */}
      <NotificationPrompt />

      {/* Benefits */}
      <Card className="border-zinc-200 rounded-2xl p-6 card-soft">
        <div className="flex items-center gap-2 mb-4">
          <Sparkle weight="fill" className="text-amber-500" size={18}/>
          <div className="font-display text-lg font-semibold">Seus benefícios</div>
        </div>
        <ul className="space-y-2.5 text-sm">
          <li className="flex items-start gap-2"><Dot/> Desconto de <strong className="text-emerald-700">{me.effective_discount_pct}%</strong> aplicado nos próximos pagamentos do seu plano principal.</li>
          <li className="flex items-start gap-2"><Dot/> Acesso ao status <strong>{me.loyalty_tier.toUpperCase()}</strong> com prioridade no atendimento.</li>
          <li className="flex items-start gap-2"><Dot/> Lembretes automáticos antes do vencimento de cada plano vinculado.</li>
          {me.loyalty_tier === "king" && <li className="flex items-start gap-2"><Dot/> Brindes e campanhas exclusivas reservadas para clientes King.</li>}
        </ul>
      </Card>

      {/* Support */}
      <a
        href={`https://wa.me/${settings.whatsapp_number || "5532999103556"}?text=${encodeURIComponent(settings.whatsapp_message || "Olá!")}`}
        target="_blank"
        rel="noreferrer"
        data-testid="client-support-cta"
        className="group block"
      >
        <Card className="border-zinc-200 rounded-2xl p-5 card-soft flex items-center gap-4 hover:-translate-y-0.5 transition-transform">
          <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 text-[#25D366] grid place-items-center">
            <ChatCircleDots size={22} weight="fill"/>
          </div>
          <div className="flex-1">
            <div className="font-medium">Precisa de ajuda?</div>
            <div className="text-sm text-zinc-600">Fale com nosso suporte direto pelo WhatsApp.</div>
          </div>
          <ArrowUpRight className="text-zinc-400 group-hover:text-zinc-900 transition-colors" size={20}/>
        </Card>
      </a>
    </div>
  );
};

const Dot = () => <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0"/>;

const StatTile = ({ icon: Icon, label, value, testid }) => (
  <Card className="border-zinc-200 rounded-2xl p-4 card-soft" data-testid={testid}>
    <Icon size={18} weight="duotone" className="text-blue-600"/>
    <div className="text-[10px] tracking-widest uppercase text-zinc-500 mt-3">{label}</div>
    <div className="font-display text-base sm:text-lg font-semibold mt-1 leading-tight">{value}</div>
  </Card>
);

export default ClientHome;
